import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import FloatLabelFormTextarea from '@/matter/components/matter-dialogs/FloatLabelFormTextArea'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSkillCreated, trackSkillEdited } from '@/analytics/utils/skill-analytics'
import { ApiError } from '@/util/errors/api-error'
import type { UserSkillWrite } from '@/user-skills/schemas'

type UserSkillEditorProps = {
  skill: UserSkillWrite | null
  skillId?: string
  onClose: () => void
  onSave: (content: UserSkillWrite) => Promise<string | void>
}

type SkillEditorForm = {
  name: string
  description: string
}

const kMaxSkillNameChars = 100

export default function UserSkillEditor({ skill, skillId, onClose, onSave }: UserSkillEditorProps) {
  const { trackEvent } = useAnalytics()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const { register, watch } = useForm<SkillEditorForm>({
    defaultValues: {
      name: skill?.name ?? '',
      description: skill?.description ?? '',
    },
  })

  const name = watch('name')
  const description = watch('description')
  const isNameOverLimit = Array.from(name).length > kMaxSkillNameChars
  const canSave = Boolean(name.trim() && description.trim()) && !isNameOverLimit && !saving
  const nameError = isNameOverLimit ? `Skill names must be ${kMaxSkillNameChars} characters or fewer.` : undefined

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setSaveError(null)
    try {
      const createdSkillId = await onSave({ name, description })
      if (skill && skillId) trackSkillEdited(trackEvent, skillId)
      else if (!skill && createdSkillId) trackSkillCreated(trackEvent, createdSkillId)
      onClose()
    } catch (error) {
      setSaveError(
        error instanceof ApiError && (error.status === 422 || error.status === 409)
          ? error.message
          : 'Failed to save your skill. Your text is still here — try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal open onOpenChange={(open) => !open && !saving && onClose()}>
        <ModalContent
          title={skill ? 'Edit skill' : 'Create new skill'}
          titleAlign="center"
          width="lg"
          height="full"
          className="max-w-[568px]"
          bodyClassName="flex min-h-0 flex-col overflow-hidden"
          preventDismiss={saving}
          footer={
            <ModalFooterWithActions
              inlineMessage={saveError ? { variant: 'error', children: saveError } : undefined}
              secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: saving }}
              primaryAction={{ label: skill ? 'Save' : 'Create', onClick: handleSave, loading: saving, disabled: !canSave }}
            />
          }
        >
          {!skill && (
            <div className="mb-4 shrink-0 text-sm text-brand-neutral-950 opacity-80">
              <p>Skills teach Paxton to complete specific tasks in a repeatable way.</p>
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <InputFieldFloatingLabel id="skill-name" label="Skill name" error={nameError} disabled={saving} registration={register('name')} />
            <FloatLabelFormTextarea
              id="skill-description"
              label="Instructions"
              disabled={saving}
              rows={6}
              className="min-h-0 flex-1"
              registration={register('description')}
            />
          </div>
        </ModalContent>
      </Modal>
    </>
  )
}
